import type {
  InsightsWallboardHero,
  InsightsWallboardLinesResponse,
  InsightsWallboardMeasureTile,
  InsightsWallboardReconciliationResponse,
} from "@shared-core/data/api-types";
import {
  formatCurrencyGbp,
  formatCount,
  formatDateOrDash,
} from "@shared-core/domain/formatters";

// Operational Wallboard Reconciliation — faithful to the settled design
// reference (docs/features/bi-native-analysis/charts/wallboard-reconciliation-native.html):
// a hero "are-we-on-track" strip, the six headline measure tiles, and — on tile
// click — a drill-to-lines table that ties out to the tile. Estate-wide (ties to
// Sage), so there is no scope selector (waived).
//
// Presentational only: the server composes hero + tiles + lines; this component
// formats and lays them out. Drilldown data is loaded on demand by the tab and
// passed in via `drilldown` — the view owns no fetching.

export type WallboardDrilldownState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; lines: InsightsWallboardLinesResponse };

// The drilldown table renders at most this many rows on-screen; the full
// population (which can be thousands for WIP) is available via Download CSV.
const ON_SCREEN_LINE_LIMIT = 50;

export interface WallboardReconciliationViewProps {
  data: InsightsWallboardReconciliationResponse;
  openMeasureKey: string | null;
  drilldown: WallboardDrilldownState | null;
  onToggleMeasure: (measureKey: string) => void;
  onDownloadCsv: () => void;
  isDownloadingCsv: boolean;
}

export function WallboardReconciliationView({
  data,
  openMeasureKey,
  drilldown,
  onToggleMeasure,
  onDownloadCsv,
  isDownloadingCsv,
}: WallboardReconciliationViewProps) {
  // `as_of_date` is the mart's freshness signal, set by the backend only
  // when the wallboard_measures view exists (see the API schema's
  // docstring). It briefly comes back null for a few minutes every hour
  // while the scheduled BI refresh rebuilds the view — genuinely zero data
  // is a real, populated response with today's date, so this is the one
  // reliable way to tell "nothing to show yet" apart from "the numbers are
  // actually zero" rather than reading it off a hero showing bare £0s.
  const isRefreshing = data.as_of_date === null;

  return (
    <div className="flex flex-col gap-6">
      <TopBar asOfDate={data.as_of_date} />

      {isRefreshing ? (
        <RefreshingNotice />
      ) : (
        <>
          <Hero hero={data.hero} />

          <div>
            <p className="text-muted-foreground mb-3 text-xs font-semibold tracking-wider uppercase">
              Headline measures — click to drill down
            </p>
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3">
              {data.tiles.map((tile) => (
                <MeasureTile
                  key={tile.measure_key}
                  tile={tile}
                  isOpen={openMeasureKey === tile.measure_key}
                  onClick={() => onToggleMeasure(tile.measure_key)}
                />
              ))}
            </div>
          </div>
        </>
      )}

      {!isRefreshing && openMeasureKey && drilldown ? (
        <Drilldown
          state={drilldown}
          onDownloadCsv={onDownloadCsv}
          isDownloadingCsv={isDownloadingCsv}
        />
      ) : null}

      <Footnote />
    </div>
  );
}

function TopBar({ asOfDate }: { asOfDate: string | null | undefined }) {
  return (
    <div className="border-border flex flex-wrap items-end justify-between gap-4 border-b pb-4">
      <div className="flex flex-col gap-1">
        <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
          Finance &amp; Ops
        </span>
        <h2 className="text-foreground text-lg font-semibold">
          Wallboard Reconciliation
        </h2>
        <p className="text-muted-foreground max-w-prose text-sm">
          Six headline measures over{" "}
          <code className="text-xs">fct_chargeable_work</code>, each tying out
          to the public wallboard and Sage. Click a measure to reconcile it
          against its underlying lines.
        </p>
      </div>
      <Freshness asOfDate={asOfDate} />
    </div>
  );
}

function Freshness({ asOfDate }: { asOfDate: string | null | undefined }) {
  return (
    <span className="text-muted-foreground inline-flex items-center gap-2 text-xs">
      <span
        className={`inline-block h-1.5 w-1.5 rounded-full ${asOfDate ? "bg-green-500" : "bg-amber-500"}`}
      />
      {asOfDate ? `Updated ${formatIsoDate(asOfDate)}` : "Refreshing…"}
    </span>
  );
}

// Shown in place of the hero + tiles while the hourly BI refresh is rebuilding
// wallboard_measures (a few minutes past the hour) — see the isRefreshing
// comment above. Distinct from the drilldown's own loading/error states,
// which are about one measure's lines, not the whole tab.
function RefreshingNotice() {
  return (
    <div className="border-border bg-surface text-muted-foreground flex min-h-[240px] flex-col items-center justify-center gap-2 rounded-xl border p-8 text-center text-sm">
      <span className="text-foreground font-medium">
        Figures are refreshing
      </span>
      <p className="max-w-prose">
        This happens briefly every hour while the underlying data rebuilds — it
        isn’t a real zero. Check back in a few minutes.
      </p>
    </div>
  );
}

const STATUS_META: Record<
  string,
  { label: string; bg: string; text: string; bar: string }
> = {
  ahead: {
    label: "Ahead",
    bg: "bg-green-500/10",
    text: "text-green-700 dark:text-green-400",
    bar: "bg-green-500",
  },
  on_track: {
    label: "On track",
    bg: "bg-green-500/10",
    text: "text-green-700 dark:text-green-400",
    bar: "bg-primary",
  },
  behind: {
    label: "Behind",
    bg: "bg-red-500/10",
    text: "text-red-700 dark:text-red-400",
    bar: "bg-red-500",
  },
};

function Hero({ hero }: { hero: InsightsWallboardHero }) {
  const status = STATUS_META[hero.status] ?? STATUS_META.on_track;
  const pct = Math.min(100, Math.max(0, hero.progress_pct));
  const gapNegative = hero.gap_to_target < 0;
  return (
    <div className="border-border bg-surface grid grid-cols-1 items-center gap-7 rounded-xl border p-6 md:grid-cols-[1.4fr_1fr]">
      <div>
        <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
          Invoiced MTD
        </span>
        <div className="mt-2 flex items-baseline gap-3.5">
          <span className="text-foreground font-mono text-4xl font-semibold tabular-nums">
            {formatCurrencyGbp(hero.invoiced_mtd)}
          </span>
          <span className="text-muted-foreground text-base tabular-nums">
            of {formatCurrencyGbp(hero.monthly_target)} target
          </span>
        </div>
        <div className="mt-3.5 h-2.5 overflow-hidden rounded-full bg-black/5 dark:bg-white/10">
          <div
            className={`h-full rounded-full transition-all duration-500 ${status.bar}`}
            style={{ width: `${pct}%` }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Invoiced MTD against target"
          />
        </div>
      </div>
      <div className="border-border md:border-l md:pl-7">
        <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
          Gap to target
        </span>
        <div
          className={
            "mt-2 font-mono text-2xl font-semibold tabular-nums " +
            (gapNegative ? "text-red-700 dark:text-red-400" : "text-foreground")
          }
        >
          {gapNegative ? "−" : "+"}
          {formatCurrencyGbp(Math.abs(hero.gap_to_target))}
        </div>
        <span
          className={`mt-2.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${status.bg} ${status.text}`}
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
          {status.label} · day {hero.working_day} of{" "}
          {hero.working_days_in_month}
        </span>
      </div>
    </div>
  );
}

function MeasureTile({
  tile,
  isOpen,
  onClick,
}: {
  tile: InsightsWallboardMeasureTile;
  isOpen: boolean;
  onClick: () => void;
}) {
  const value = tile.is_count
    ? formatCount(tile.value)
    : formatCurrencyGbp(tile.value);
  const stripe = tile.is_count ? "bg-amber-500" : "bg-primary";
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={isOpen}
      className={
        "bg-surface relative rounded-xl border p-4 pl-5 text-left transition-shadow " +
        (isOpen
          ? "border-primary ring-primary/40 ring-1"
          : "border-border hover:border-border hover:shadow-sm")
      }
    >
      <span
        className={`absolute top-4 bottom-4 left-0 w-[3px] rounded-full ${stripe}`}
      />
      <span className="flex items-center justify-between">
        <span className="text-muted-foreground text-sm font-medium">
          {tile.measure_label}
        </span>
        <ChevronDown open={isOpen} />
      </span>
      <span className="text-foreground mt-2.5 block font-mono text-2xl font-semibold tabular-nums">
        {value}
      </span>
      <span className="text-muted-foreground mt-1.5 block text-xs">
        {tile.is_count ? "distinct jobs" : "value in scope"}
      </span>
    </button>
  );
}

function ChevronDown({ open }: { open: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      className={
        "shrink-0 transition-transform " +
        (open ? "text-primary rotate-180" : "text-muted-foreground")
      }
      aria-hidden="true"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

const COLUMNS: Array<{
  key: keyof InsightsWallboardLinesResponse["lines"][number];
  label: string;
  numeric?: boolean;
}> = [
  { key: "row_kind", label: "Kind" },
  { key: "job_no", label: "Job" },
  { key: "order_no", label: "Order" },
  { key: "customer_code", label: "Cust" },
  { key: "customer_name", label: "Customer" },
  { key: "product_code", label: "Product" },
  { key: "description", label: "Description" },
  { key: "status", label: "Status" },
  { key: "ordered_at", label: "Ordered" },
  { key: "due_at", label: "Due" },
  { key: "price_agreed", label: "Price agreed", numeric: true },
  { key: "invoiced", label: "Invoiced", numeric: true },
];

function Drilldown({
  state,
  onDownloadCsv,
  isDownloadingCsv,
}: {
  state: WallboardDrilldownState;
  onDownloadCsv: () => void;
  isDownloadingCsv: boolean;
}) {
  if (state.status === "loading") {
    return (
      <div className="border-primary bg-surface text-muted-foreground rounded-xl border p-8 text-center text-sm">
        Loading reconciling lines…
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="border-primary bg-surface text-destructive rounded-xl border p-8 text-center text-sm">
        Couldn’t load the reconciling lines.
      </div>
    );
  }

  const { lines } = state;
  const total = lines.is_count
    ? `${formatCount(lines.total_value)} distinct jobs`
    : `${formatCurrencyGbp(lines.total_value)} total`;
  // Cap the on-screen table; the full population is one click away via CSV.
  const shown = lines.lines.slice(0, ON_SCREEN_LINE_LIMIT);
  const hasMoreThanShown = lines.line_count > shown.length;

  return (
    <div className="border-primary bg-surface overflow-hidden rounded-xl border">
      <div className="border-border bg-accent flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <span className="text-foreground text-sm font-semibold">
          {lines.measure_label} — reconciling lines
        </span>
        <div className="flex items-center gap-3">
          <span className="text-muted-foreground text-xs tabular-nums">
            {formatCount(lines.line_count)} line
            {lines.line_count === 1 ? "" : "s"} · {total}
            {hasMoreThanShown ? ` · showing first ${shown.length}` : ""}
          </span>
          <button
            type="button"
            onClick={onDownloadCsv}
            disabled={isDownloadingCsv}
            className="border-border text-foreground hover:bg-muted/60 inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-60"
          >
            {isDownloadingCsv ? "Preparing…" : "Download CSV"}
          </button>
        </div>
      </div>
      {lines.lines.length === 0 ? (
        <div className="text-muted-foreground p-8 text-center text-sm">
          No lines for this measure.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground border-border border-b">
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    className={
                      "px-3 py-2.5 font-semibold tracking-wide uppercase whitespace-nowrap " +
                      (c.numeric ? "text-right" : "text-left")
                    }
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((line, i) => (
                <tr
                  key={`${line.job_no ?? line.order_no ?? "row"}-${i}`}
                  className="border-border/60 hover:bg-muted/40 border-b last:border-0"
                >
                  {COLUMNS.map((c) => (
                    <LineCell key={c.key} line={line} column={c} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="border-border text-muted-foreground border-t px-4 py-2.5 text-xs">
        {hasMoreThanShown ? (
          <>
            Showing the {shown.length} largest of{" "}
            {formatCount(lines.line_count)} lines — <b>Download CSV</b> for
            every line. The full population sums to the{" "}
            <b>{lines.measure_label}</b> tile above.
          </>
        ) : (
          <>
            Every line the <b>{lines.measure_label}</b> headline counts — the
            sum here ties out to the tile above by construction (same measure
            predicate).
          </>
        )}
      </div>
    </div>
  );
}

function LineCell({
  line,
  column,
}: {
  line: InsightsWallboardLinesResponse["lines"][number];
  column: (typeof COLUMNS)[number];
}) {
  const raw = line[column.key];
  if (column.key === "status") {
    return (
      <td className="px-3 py-2.5 whitespace-nowrap">
        <StatusChip status={line.status} />
      </td>
    );
  }
  if (column.key === "job_no") {
    return (
      <td className="text-primary px-3 py-2.5 font-mono whitespace-nowrap">
        {line.job_no ?? "—"}
      </td>
    );
  }
  if (column.numeric) {
    return (
      <td className="text-foreground px-3 py-2.5 text-right font-mono whitespace-nowrap tabular-nums">
        {typeof raw === "number" && raw !== 0 ? formatCurrencyGbp(raw) : "—"}
      </td>
    );
  }
  return (
    <td className="text-foreground px-3 py-2.5 whitespace-nowrap">
      {raw === null || raw === undefined || raw === "" ? "—" : String(raw)}
    </td>
  );
}

const STATUS_CHIP: Record<string, string> = {
  in_progress: "bg-primary/10 text-primary",
  produced: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
  delivered: "bg-green-500/10 text-green-700 dark:text-green-400",
  invoiced: "bg-muted text-muted-foreground",
};

function StatusChip({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const cls = STATUS_CHIP[status] ?? "bg-muted text-muted-foreground";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${cls}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}

function Footnote() {
  return (
    <div className="border-border text-muted-foreground flex flex-wrap gap-x-5 gap-y-1.5 border-t pt-4 text-xs">
      <span>
        <b className="text-muted-foreground font-semibold">Grain:</b> one row
        per chargeable-work ledger line
      </span>
      <span>
        <b className="text-muted-foreground font-semibold">RLS:</b> estate-wide
        — ties out to Sage, so not portfolio-scoped
      </span>
      <span>
        <b className="text-muted-foreground font-semibold">Measure defs:</b>{" "}
        single source — <code>wallboard_measures</code> (dbt)
      </span>
    </div>
  );
}

function formatIsoDate(iso: string): string {
  // ISO date (YYYY-MM-DD) → "9 Jul 2026". Guard against a bad value.
  const formatted = formatDateOrDash(iso);
  return formatted === "-" ? iso : formatted;
}
